/**
 * Default Vehicle
 */
import { gql } from '@apollo/client';
import { reduce, get, isEmpty } from 'lodash-es';
import { createModelSegment } from 'client/data/luckdragon/segment';
import { EdmundsAPI } from 'client/data/api/api-client';
import { EdmundsGraphQLFederation } from 'client/data/graphql/graphql-client';
import { withMetrics } from 'client/data/api/api-metrics';
import { FeatureFlagModel } from 'client/data/models/feature-flag';
import { getSupportedGraphqlModelYear } from './vehicle-model-year';

// Return vehicle or default submodel
export function buildDefaultSubmodelPath({ make, model, year, submodel }) {
  return submodel
    ? `makes["${make}"].models["${model}"].submodels["${submodel}"].years["${year}"]`
    : `makes["${make}"].models["${model}"].defaultSubmodelSlugAndPubState.years["${year}"]`;
}

export function buildMakeModelDefaultYear({ make, model }) {
  return `makes["${make}"].models["${model}"].defaultYear`;
}

function generateSlugs(items, slugName = 'slug') {
  if (items && typeof items === 'object') {
    return reduce(
      items,
      (result, value, key) => {
        if (typeof value === 'string') {
          return {
            ...result,
            [key]: value,
          };
        }
        const withSlugs = {
          ...result,
          [key]: {
            ...value,
            [slugName]: key,
          },
        };

        if (value.submodels) {
          withSlugs[key].submodels = generateSlugs(value.submodels);
        }
        return withSlugs;
      },
      {}
    );
  }
  return items;
}

export const VehicleDefaultModel = createModelSegment(
  'vehicle',
  [
    /**
     * @see buildDefaultSubmodelPath
     */
    {
      path: 'makes["{make}"].models["{model}"].defaultSubmodelSlugAndPubState.years["{year}"]',
      async resolve({ make, model, year }, context) {
        const modelYear = getSupportedGraphqlModelYear(year);
        if (modelYear === null) {
          return null;
        }

        const extendedApiCache = await context.resolveValue('extendedApiCache', FeatureFlagModel);
        return withMetrics(EdmundsGraphQLFederation, context)
          .query(
            gql`
              query ($make: String!, $model: String!, $year: Int!) {
                modelYears(makeSlug: $make, modelSlug: $model, year: $year) {
                  submodels(sortBy: { shareBySales: DESC }, page: { pageSize: 1 }) {
                    slug
                    publicationStates
                  }
                }
              }
            `,
            {
              make,
              model,
              year: modelYear,
            },
            {
              useVenomCache: !!extendedApiCache,
            }
          )
          .then(response => {
            const pubStates = {};
            get(response, 'modelYears[0].submodels[0].publicationStates', []).forEach(pub => {
              pubStates[pub] = true;
            });

            return { slug: get(response, 'modelYears[0].submodels[0].slug'), pubStates };
          });
      },
    },
    /**
     * @see buildDefaultSubmodelPath
     */
    {
      path: 'makes["{makeSlug}"].models["{modelSlug}"].submodels["{submodelSlug}"].years',
      async resolve({ makeSlug, modelSlug, submodelSlug }, context) {
        const submodelPath = `makes["${makeSlug}"].models["${modelSlug}"].submodels["${submodelSlug}"]`;
        return context.resolveValue(submodelPath).then(async submodel => {
          if (submodel) {
            const url = `/vehicle/v4/makes/${makeSlug}/models/${modelSlug}/submodels/${submodelSlug}/years/`;
            const extendedApiCache = await context.resolveValue('extendedApiCache', FeatureFlagModel);

            return withMetrics(EdmundsAPI, context)
              .fetchJson(url, { timeout: 1000, useVenomCache: extendedApiCache }) // Added for VDP and Core Cache
              .then(({ results }) => generateSlugs(results, 'year'));
          }

          return {};
        });
      },
    },
    /**
     * Example: https://qa-21-www.edmunds.com/gateway/api/coreresearch/v1/defaultyear/honda/civic
     * @see buildMakeModelDefaultYear
     */
    {
      path: 'makes["{makeSlug}"].models["{modelSlug}"].defaultYear',
      async resolve({ makeSlug, modelSlug }, context) {
        const url = `/coreresearch/v1/defaultyear/${makeSlug}/${modelSlug}`;
        const extendedApiCache = await context.resolveValue('extendedApiCache', FeatureFlagModel);

        return withMetrics(EdmundsAPI, context)
          .fetchJson(url, { timeout: 1000, useVenomCache: extendedApiCache }) // Added for VDP and Core Cache
          .then(years => {
            if (isEmpty(years)) {
              return null;
            }

            return years.results.year || years.results.defaultYear;
          });
      },
    },
  ],
  true // allowMergeToExisting
);
